import React, { useState, useEffect, useCallback } from 'react';
import axios, { API } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Shield, Users as UsersIcon, KeyRound, Trash2, CheckCircle, Copy, Send } from 'lucide-react';
import { toast } from 'sonner';

const AdminUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/users`);
      setUsers(resp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const confirmReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const resp = await axios.post(`${API}/admin/users/${resetTarget.id}/reset-password`);
      setResetResult(resp.data);
      toast.success('Temporary password generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
      setResetTarget(null);
    } finally {
      setResetting(false);
    }
  };

  const closeResetDialog = () => { setResetTarget(null); setResetResult(null); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.id}`);
      toast.success(`User "${deleteTarget.username}" deleted`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const filtered = search.trim()
    ? users.filter((u) => u.username.toLowerCase().includes(search.trim().toLowerCase()))
    : users;

  const buildResetEmailLink = (username, tempPassword) => {
    const subject = encodeURIComponent('Just B - Your temporary password');
    const body = encodeURIComponent(
      `Hi ${username},\n\nI've reset your Just B password. Your temporary password is:\n\n${tempPassword}\n\nPlease log in and, once you're in, reach out if you'd like a new one set.\n\nThanks for being a beta tester!\n\n— Brandy`
    );
    return `mailto:?subject=${subject}&body=${body}`;
  };

  const renderUserList = () => {
    if (loading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-justb-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      );
    }

    if (filtered.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">
              {search ? 'No users match your search.' : 'No users yet. Invite beta testers to get started!'}
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-2">
        {filtered.map((u) => {
          const joinedStr = u.created_date ? new Date(u.created_date).toLocaleDateString() : '\u2014';
          return (
            <Card key={u.id}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{u.username}</span>
                      {u.is_admin && (
                        <Badge variant="outline" className="text-justb-primary border-justb-primary/40">Admin</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Joined {joinedStr} &middot; {u.active_goals} active goal{u.active_goals === 1 ? '' : 's'} &middot; {u.completed_tasks} completed task{u.completed_tasks === 1 ? '' : 's'}
                    </p>
                  </div>
                  {!u.is_admin && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setResetTarget(u); setResetResult(null); }}
                        className="text-justb-primary border-justb-primary/40 hover:bg-justb-primary/5"
                        data-testid={`reset-password-btn-${u.id}`}
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                        Reset password
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteTarget(u)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        data-testid={`delete-user-btn-${u.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">For admin use only — handle user data responsibly.</p>
          <p className="text-amber-800">Use "Reset password" only when a user has emailed you asking for it.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-justb-primary" />
            Users
          </h2>
          <p className="text-sm text-gray-500">{users.length} total user{users.length === 1 ? '' : 's'}</p>
        </div>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search username…" className="sm:max-w-xs" />
      </div>

      {renderUserList()}

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && closeResetDialog()}>
        <DialogContent className="sm:max-w-[500px]">
          {!resetResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5 text-justb-primary" />
                  Reset password for {resetTarget?.username}?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  This will create a new temporary password for <strong>{resetTarget?.username}</strong> and immediately invalidate their current one.
                </p>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  Only do this if the user actually asked you to reset their password.
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={closeResetDialog} disabled={resetting}>Cancel</Button>
                  <Button onClick={confirmReset} disabled={resetting} className="bg-justb-primary hover:bg-justb-primary/90">
                    {resetting ? 'Resetting…' : 'Generate new password'}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  Password reset for {resetResult.username}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  Copy this temporary password and send it to the user. <strong>You won't see it again after closing this window.</strong>
                </p>
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <code className="flex-1 font-mono text-base text-gray-900 select-all break-all">{resetResult.temporary_password}</code>
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(resetResult.temporary_password)}>
                    <Copy className="h-4 w-4 mr-1" />Copy
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-end">
                  <a href={buildResetEmailLink(resetResult.username, resetResult.temporary_password)} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-justb-primary hover:bg-justb-primary/90 text-white text-sm font-medium">
                    <Send className="h-4 w-4" />Open email to user
                  </a>
                  <Button variant="outline" onClick={closeResetDialog}>Close</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete user dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-[450px]" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Delete user {deleteTarget?.username}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              This will <strong>permanently delete</strong> the account for <strong>{deleteTarget?.username}</strong> along with all their data:
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>All goals</li>
              <li>All daily tasks and reflections</li>
              <li>All feedback submissions</li>
            </ul>
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900">This action cannot be undone.</div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="delete-user-cancel-btn">Cancel</Button>
              <Button onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-user-confirm-btn">
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersTab;
